import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { ICommissionThreshold, INewCommissionDTO } from 'src/app/features/agents/models/commissions.models';
import { rangeValidator } from 'src/app/shared/validators/fields-validators';

@Component({
  selector: 'app-new-commission-thresholds',
  templateUrl: './new-commission-thresholds.component.html',
  styleUrls: ['./new-commission-thresholds.component.scss'],
})
export class NewCommissionThresholdsComponent implements OnInit {
  @Input() commission!: INewCommissionDTO;
  @Output() nextStep = new EventEmitter<Partial<INewCommissionDTO>>();
  @Output() previousStep = new EventEmitter<Partial<INewCommissionDTO>>();

  @ViewChild('thresholdsArray') thresholdsArray!: ElementRef;

  thresholdForm!: FormGroup;
  thresholdFormArray!: FormArray<any>;

  destroy$ = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    this.thresholdFormArray = new FormArray([]) as any;

    this.thresholdForm = new FormGroup({
      thresholds: this.thresholdFormArray,
    });

    this.thresholdFormArray.valueChanges.pipe(debounceTime(500), takeUntil(this.destroy$), distinctUntilChanged()).subscribe((values) => {
      this.updateThresholdValidators();
    });

    if (this.commission?.thresholds?.length) {
      this.commission?.thresholds.map((threshold: ICommissionThreshold) =>
        this.addThreshold(threshold.min_order_value, threshold.max_order_value, threshold.new_commission)
      );
    }
  }

  private updateThresholdValidators(): void {
    this.thresholdFormArray.controls.forEach((control, index) => {
      const currentThreshold = this.thresholdFormArray.controls[index];
      const minCurrentThreshold = currentThreshold?.get('min_order_value')?.value || 0;
      if (index !== 0) {
        const previousThreshold = this.thresholdFormArray.controls[index - 1];
        const maxPreviousThreshold = previousThreshold.get('max_order_value')?.value;

        if (maxPreviousThreshold !== undefined && maxPreviousThreshold !== null) {
          control.get('min_order_value')?.setValidators([
            Validators.required,
            Validators.min(maxPreviousThreshold > 0 ? parseFloat(parseFloat(maxPreviousThreshold + 0.01).toFixed(2)) : 0),
          ]);
          control.get('min_order_value')?.updateValueAndValidity();

          const maxValidators = [
            Validators.min(minCurrentThreshold > 0 ? parseFloat(parseFloat(minCurrentThreshold + 0.01).toFixed(2)) : 0),
          ]
          if(index !== this.thresholdFormArray.length - 1)
          {
            maxValidators.push(Validators.required);
          }

          control.get('max_order_value')?.setValidators(maxValidators);
          control.get('max_order_value')?.updateValueAndValidity();
        }
      }
      else{
        const maxValidators =[
            Validators.min(minCurrentThreshold > 0 ? parseFloat(parseFloat(minCurrentThreshold + 0.01).toFixed(2)) : 0),
          ];
          if(this.thresholdFormArray.length > 1)
          {
            maxValidators.push(Validators.required)
          }
          control.get('max_order_value')?.setValidators(maxValidators);
          control.get('max_order_value')?.updateValueAndValidity();
      }
    });
  }

  get thresholds(): FormArray {
    return this.thresholdForm.get('thresholds') as FormArray;
  }

  addThreshold(min: number | null = null, max: number | null = null, new_commission: number = 0): void {
    const previousThreshold = this.thresholdFormArray?.controls[this.thresholdFormArray.length - 1];
    const minPreviousThreshold = previousThreshold?.get('min_order_value')?.value || 0;
    const maxPreviousThreshold = previousThreshold?.get('max_order_value')?.value || minPreviousThreshold;

    const thresholdFormGroup = new FormGroup(
      {
        min_order_value: new FormControl(min, [Validators.required, Validators.min(maxPreviousThreshold > 0 ? maxPreviousThreshold + 1 : 0)]),
        max_order_value: new FormControl(max, [Validators.required, Validators.min(maxPreviousThreshold > 0 ? maxPreviousThreshold + 1 : 0)]),
        new_commission: new FormControl(new_commission, [Validators.required, Validators.min(0), Validators.max(100)]),
      },
      { validators: rangeValidator("min_order_value", "max_order_value") }
    );
    this.thresholdFormArray.push(thresholdFormGroup);

    this.updateThresholdValidators()

    if (this.thresholdsArray) {
      this.thresholdsArray.nativeElement.scrollTop = this.thresholdsArray.nativeElement.scrollHeight;
    }
  }

  removeThreshold(index: number): void {
    this.thresholdFormArray.removeAt(index);
  }

  onInputRemoveLeadingZero(event: Event, formGroup: any, formControlName: string) {
    const input = event.target as HTMLInputElement;
    if (/^0[0-9]+/.test(input.value)) {
      input.value = input.value.replace(/^0+/, '');
      formGroup.get(formControlName)?.setValue(input.value);
    }
  }

  next() {
    if (!this.thresholdForm.valid) {
      this.thresholdForm.markAllAsTouched();
      return;
    }

    const stepPayload = this.thresholdForm.getRawValue();
    console.log('Threshold', stepPayload);
    this.nextStep.emit(stepPayload);
  }

  previous() {
    const stepPayload = this.thresholdForm.getRawValue();
    console.log('Threshold', stepPayload);
    this.previousStep.emit(stepPayload);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
